import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * The installed ride app is granted the scope /ride/<handle>/ and nothing else. A bare
 * /r/<token>, /track/<token>, /receipt/<token> or /pay/<token> URL sits outside it, so tapping one
 * inside the app threw the passenger into a browser window that looked like a logged-out app.
 */
export const ridePath = (handle, suffix = "") => `/ride/${handle}/${suffix.replace(/^\//, "")}`;

// The other installed apps own their own scopes (/portal/*, /app/*, /admin). A passenger token page
// opened INSIDE one of those must stay there rather than jumping into the rider app.
const OTHER_APPS = /^\/(portal|app|admin)\b/;

/** A sibling passenger page inside whichever app the visitor is already using. */
export const siblingPath = (pathname, handle, suffix) =>
  pathname.startsWith("/portal/client")
    ? `/portal/client/${suffix.replace(/^\//, "")}`
    : ridePath(handle, suffix);

/** Once we know which operator the token belongs to, move the passenger back inside the app. */
export const useRiderScope = (handle, suffix) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (!handle || !suffix) return;
    if (OTHER_APPS.test(pathname)) return;
    const target = ridePath(handle, suffix);
    if (pathname === target || pathname === target.replace(/\/$/, "")) return;
    navigate(`${target}${search}`, { replace: true });
  }, [handle, suffix, pathname, search, navigate]);
};

// The passenger's sign-in is stored per operator, never in a slot another app could read. The same
// session is written under the company id and the handle so /ride/<id> and /ride/<handle> agree.
const slot = (key) => `rider_session_${key}`;

export const saveRiderSession = (keys, session) =>
  keys.filter(Boolean).forEach((k) => localStorage.setItem(slot(k), JSON.stringify(session)));

export const readRiderSession = (keys) => {
  for (const key of keys.filter(Boolean)) {
    try {
      const session = JSON.parse(localStorage.getItem(slot(key)) || "null");
      if (session?.token) return session;
    } catch {
      localStorage.removeItem(slot(key));
    }
  }
  return null;
};

export const clearRiderSession = (keys) =>
  keys.filter(Boolean).forEach((k) => localStorage.removeItem(slot(k)));
